import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowDown,
  ArrowUp,
  FileText,
  Link as LinkIcon,
  Pencil,
  Plus,
  Radio,
  Send,
  Trash2,
  Video,
  Archive,
} from 'lucide-react';
import {
  LmsCourse,
  LmsCoursesApi,
  LmsLesson,
  LmsLessonType,
  LmsLessonsApi,
  LmsModule,
  LmsModulesApi,
} from '@/services/lms.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Input';
import { toast } from '@/stores/toast.store';
import { usePermissions } from '@/hooks/usePermissions';
import { CourseFormModal } from './LmsCoursesPage';
import { LessonSubmitValues, LmsLessonFormModal } from './LmsLessonFormModal';
import { LmsCourseEnrollmentsTab } from './LmsCourseEnrollmentsTab';
import { LmsCourseAssignmentsTab } from './LmsCourseAssignmentsTab';
import { LmsQuizzesTab } from './LmsQuizzesTab';

const statusTone = { draft: 'slate', published: 'green', archived: 'amber' } as const;

type BuilderTab = 'content' | 'enrollments' | 'assignments' | 'quizzes';
const TABS: { key: BuilderTab; label: string }[] = [
  { key: 'content', label: 'Content' },
  { key: 'assignments', label: 'Assignments' },
  { key: 'quizzes', label: 'Quizzes' },
  { key: 'enrollments', label: 'Enrollments' },
];

const LESSON_ICON: Record<LmsLessonType, typeof Video> = {
  video: Video,
  document: FileText,
  text: FileText,
  link: LinkIcon,
  live: Radio,
};

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}

export function LmsCourseBuilderPage() {
  const { id: courseId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { isAdmin } = usePermissions();
  const [editOpen, setEditOpen] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [newModuleTitle, setNewModuleTitle] = useState('');

  // Tab state lives in the URL (?tab=enrollments) so the builder is
  // deep-linkable and a reload lands back on the same tab — no existing
  // page-tab convention persists to the URL (StudentViewPage/StaffViewPage
  // use local useState), so this is a deliberate, minimal addition using
  // react-router's own useSearchParams rather than a new dependency.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: BuilderTab =
    tabParam === 'enrollments'
      ? 'enrollments'
      : tabParam === 'assignments'
        ? 'assignments'
        : tabParam === 'quizzes'
          ? 'quizzes'
          : 'content';
  const setTab = (t: BuilderTab) => {
    const next = new URLSearchParams(searchParams);
    if (t === 'content') next.delete('tab');
    else next.set('tab', t);
    setSearchParams(next, { replace: true });
  };

  const { data: course, isLoading: courseLoading } = useQuery({
    queryKey: ['lms-course', courseId],
    queryFn: () => LmsCoursesApi.get(courseId!),
    enabled: !!courseId,
  });

  const { data: modules = [], isLoading: modulesLoading } = useQuery({
    queryKey: ['lms-modules', courseId],
    queryFn: () => LmsModulesApi.list(courseId!),
    enabled: !!courseId && tab === 'content',
  });

  const updateCourse = useMutation({
    mutationFn: (payload: { title: string; description?: string; subject?: string }) =>
      LmsCoursesApi.update(courseId!, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lms-course', courseId] });
      qc.invalidateQueries({ queryKey: ['lms-courses'] });
      setEditOpen(false);
      toast.success('Course updated');
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Save failed'),
  });

  const publish = useMutation({
    mutationFn: () => LmsCoursesApi.publish(courseId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lms-course', courseId] });
      qc.invalidateQueries({ queryKey: ['lms-courses'] });
      toast.success('Course published');
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Publish failed'),
  });

  const archive = useMutation({
    mutationFn: () => LmsCoursesApi.archive(courseId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lms-course', courseId] });
      qc.invalidateQueries({ queryKey: ['lms-courses'] });
      setConfirmArchive(false);
      toast.success('Course archived');
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Archive failed'),
  });

  const addModule = useMutation({
    mutationFn: (title: string) => LmsModulesApi.create(courseId!, title),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lms-modules', courseId] });
      setNewModuleTitle('');
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Could not add module'),
  });

  const reorderModules = useMutation({
    mutationFn: (orderedModuleIds: string[]) => LmsModulesApi.reorder(courseId!, orderedModuleIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lms-modules', courseId] }),
    onError: (e) => toast.error(errMsg(e) ?? 'Reorder failed'),
  });

  const moveModule = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= modules.length) return;
    const ids = modules.map((m) => m.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorderModules.mutate(ids);
  };

  if (courseLoading) {
    return <div className="p-8 text-center text-slate-400">Loading course…</div>;
  }
  if (!course) {
    return <div className="p-8 text-center text-slate-400">Course not found.</div>;
  }

  return (
    <>
      <button
        className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"
        onClick={() => navigate('/lms/courses')}
      >
        <ArrowLeft className="h-4 w-4" /> Back to courses
      </button>

      <PageHeader
        title={course.title}
        description={course.subject ?? undefined}
        actions={
          <div className="flex items-center gap-2">
            <Badge tone={statusTone[course.status]}>{course.status}</Badge>
            <button className="btn-secondary" onClick={() => setEditOpen(true)}>
              <Pencil className="mr-1.5 h-4 w-4" /> Edit details
            </button>
            {course.status === 'draft' && (
              <button className="btn-primary" onClick={() => publish.mutate()} disabled={publish.isPending}>
                <Send className="mr-1.5 h-4 w-4" /> Publish
              </button>
            )}
            {course.status !== 'archived' && (
              <button className="btn-secondary" onClick={() => setConfirmArchive(true)}>
                <Archive className="mr-1.5 h-4 w-4" /> Archive
              </button>
            )}
          </div>
        }
      />

      <div className="mb-4 flex gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition ${
              tab === t.key
                ? 'border-brand-500 text-brand-700'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'enrollments' ? (
        <LmsCourseEnrollmentsTab courseId={courseId!} />
      ) : tab === 'assignments' ? (
        <LmsCourseAssignmentsTab courseId={courseId!} />
      ) : tab === 'quizzes' ? (
        <LmsQuizzesTab courseId={courseId!} />
      ) : (
        <div className="space-y-4">
          {modulesLoading ? (
            <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-slate-400">
              Loading modules…
            </div>
          ) : modules.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-slate-400">
              No modules yet. Add one below to get started.
            </div>
          ) : (
            modules.map((mod, index) => (
              <ModulePanel
                key={mod.id}
                courseId={courseId!}
                module={mod}
                isFirst={index === 0}
                isLast={index === modules.length - 1}
                onMoveUp={() => moveModule(index, -1)}
                onMoveDown={() => moveModule(index, 1)}
                canDelete={isAdmin}
              />
            ))
          )}

          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white p-3">
            <Input
              value={newModuleTitle}
              onChange={(e) => setNewModuleTitle(e.target.value)}
              placeholder="New module title"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newModuleTitle.trim()) addModule.mutate(newModuleTitle.trim());
              }}
            />
            <button
              className="btn-primary shrink-0"
              disabled={!newModuleTitle.trim() || addModule.isPending}
              onClick={() => addModule.mutate(newModuleTitle.trim())}
            >
              <Plus className="mr-1.5 h-4 w-4" /> Add module
            </button>
          </div>
        </div>
      )}

      {editOpen && (
        <CourseFormModal
          open={editOpen}
          course={course}
          onClose={() => setEditOpen(false)}
          saving={updateCourse.isPending}
          onSubmit={(v) => updateCourse.mutate(v)}
        />
      )}

      <ConfirmDialog
        open={confirmArchive}
        onClose={() => setConfirmArchive(false)}
        onConfirm={() => archive.mutate()}
        loading={archive.isPending}
        title="Archive course?"
        message={`"${course.title}" will drop out of the student portal and stop accepting new enrollments.`}
        confirmText="Archive"
        destructive={false}
      />
    </>
  );
}

function ModulePanel({
  courseId,
  module: mod,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  canDelete,
}: {
  courseId: string;
  module: LmsModule;
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canDelete: boolean;
}) {
  const qc = useQueryClient();
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState(mod.title);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [lessonModal, setLessonModal] = useState<{ open: boolean; lesson?: LmsLesson }>({ open: false });
  const [confirmDeleteLesson, setConfirmDeleteLesson] = useState<LmsLesson | null>(null);

  const { data: lessons = [], isLoading: lessonsLoading } = useQuery({
    queryKey: ['lms-lessons', mod.id],
    queryFn: () => LmsLessonsApi.list(mod.id),
  });

  const rename = useMutation({
    mutationFn: (t: string) => LmsModulesApi.update(courseId, mod.id, t),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lms-modules', courseId] });
      setRenaming(false);
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Rename failed'),
  });

  const removeModule = useMutation({
    mutationFn: () => LmsModulesApi.remove(courseId, mod.id),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['lms-modules', courseId] });
      setConfirmDelete(false);
      toast.success(
        res.lessonsDeleted > 0
          ? `Module deleted (${res.lessonsDeleted} lesson${res.lessonsDeleted === 1 ? '' : 's'} deleted with it)`
          : 'Module deleted',
      );
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Delete failed'),
  });

  const upsertLesson = useMutation({
    mutationFn: (v: { id?: string; payload: LessonSubmitValues }) =>
      v.id
        ? LmsLessonsApi.update(mod.id, v.id, {
            title: v.payload.title,
            content: v.payload.content,
            isMandatory: v.payload.isMandatory,
          })
        : LmsLessonsApi.create(mod.id, v.payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lms-lessons', mod.id] });
      qc.invalidateQueries({ queryKey: ['lms-modules', courseId] });
      setLessonModal({ open: false });
      toast.success('Lesson saved');
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Save failed'),
  });

  const removeLesson = useMutation({
    mutationFn: (lessonId: string) => LmsLessonsApi.remove(mod.id, lessonId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lms-lessons', mod.id] });
      qc.invalidateQueries({ queryKey: ['lms-modules', courseId] });
      setConfirmDeleteLesson(null);
      toast.success('Lesson deleted');
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Delete failed'),
  });

  const reorderLessons = useMutation({
    mutationFn: (orderedLessonIds: string[]) => LmsLessonsApi.reorder(mod.id, orderedLessonIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lms-lessons', mod.id] }),
    onError: (e) => toast.error(errMsg(e) ?? 'Reorder failed'),
  });

  const moveLesson = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= lessons.length) return;
    const ids = lessons.map((l) => l.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorderLessons.mutate(ids);
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
        <div className="flex flex-1 items-center gap-2">
          <div className="flex flex-col">
            <button className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30" onClick={onMoveUp} disabled={isFirst} title="Move up">
              <ArrowUp className="h-3.5 w-3.5" />
            </button>
            <button className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30" onClick={onMoveDown} disabled={isLast} title="Move down">
              <ArrowDown className="h-3.5 w-3.5" />
            </button>
          </div>
          {renaming ? (
            <div className="flex flex-1 items-center gap-2">
              <Input
                autoFocus
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && title.trim()) rename.mutate(title.trim());
                  if (e.key === 'Escape') {
                    setTitle(mod.title);
                    setRenaming(false);
                  }
                }}
              />
              <button className="btn-primary" disabled={!title.trim() || rename.isPending} onClick={() => rename.mutate(title.trim())}>
                Save
              </button>
              <button className="btn-secondary" onClick={() => { setTitle(mod.title); setRenaming(false); }}>
                Cancel
              </button>
            </div>
          ) : (
            <span className="font-medium text-slate-900">{mod.title}</span>
          )}
        </div>
        {!renaming && (
          <div className="flex items-center gap-1">
            <button className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-600" onClick={() => setRenaming(true)} title="Rename">
              <Pencil className="h-4 w-4" />
            </button>
            {canDelete && (
              <button className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" onClick={() => setConfirmDelete(true)} title="Delete module">
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>

      <div className="p-4">
        {lessonsLoading ? (
          <div className="py-4 text-center text-sm text-slate-400">Loading lessons…</div>
        ) : lessons.length === 0 ? (
          <div className="py-4 text-center text-sm text-slate-400">No lessons in this module yet.</div>
        ) : (
          <ul className="space-y-1">
            {lessons.map((lesson, index) => {
              const Icon = LESSON_ICON[lesson.type];
              return (
                <li key={lesson.id} className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-slate-50">
                  <div className="flex flex-col">
                    <button
                      className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                      onClick={() => moveLesson(index, -1)}
                      disabled={index === 0}
                      title="Move up"
                    >
                      <ArrowUp className="h-3 w-3" />
                    </button>
                    <button
                      className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                      onClick={() => moveLesson(index, 1)}
                      disabled={index === lessons.length - 1}
                      title="Move down"
                    >
                      <ArrowDown className="h-3 w-3" />
                    </button>
                  </div>
                  <Icon className="h-4 w-4 shrink-0 text-slate-400" />
                  <span className="flex-1 text-sm text-slate-800">{lesson.title}</span>
                  {lesson.isMandatory && <Badge tone="blue">Mandatory</Badge>}
                  <Badge tone="slate">{lesson.type}</Badge>
                  <button
                    className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-600"
                    onClick={() => setLessonModal({ open: true, lesson })}
                    title="Edit"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
                    onClick={() => setConfirmDeleteLesson(lesson)}
                    title="Delete"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        <button className="btn-secondary mt-3" onClick={() => setLessonModal({ open: true })}>
          <Plus className="mr-1.5 h-4 w-4" /> Add lesson
        </button>
      </div>

      {lessonModal.open && (
        <LmsLessonFormModal
          open={lessonModal.open}
          lesson={lessonModal.lesson}
          onClose={() => setLessonModal({ open: false })}
          saving={upsertLesson.isPending}
          onSubmit={(v) => upsertLesson.mutate({ id: lessonModal.lesson?.id, payload: v })}
        />
      )}

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => removeModule.mutate()}
        loading={removeModule.isPending}
        title="Delete module?"
        message={`Delete "${mod.title}" and all ${mod.lessonCount} of its lesson${mod.lessonCount === 1 ? '' : 's'}.`}
        confirmText="Delete module"
      />

      <ConfirmDialog
        open={!!confirmDeleteLesson}
        onClose={() => setConfirmDeleteLesson(null)}
        onConfirm={() => confirmDeleteLesson && removeLesson.mutate(confirmDeleteLesson.id)}
        loading={removeLesson.isPending}
        title="Delete lesson?"
        message={`Delete "${confirmDeleteLesson?.title}".`}
        confirmText="Delete lesson"
      />
    </div>
  );
}
